import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { IncomeTileConfig } from "../IncomeBreakdown/IncomeBreakdown.types";
import type {
  MonthlyIncomeBreakdownResponse,
  MonthlyIncomeBreakdownCorePlanBonus,
  MonthlyIncomeBreakdownCorePlusIncentive,
  MonthlyIncomeBreakdownRequirement,
  MonthlyIncomeBreakdownRubyBonusModal,
} from "../../../../services/MonthlyPerformanceIncome/monthlyPerformanceIncomeAPI.types";
import { AWARD_CODES } from "../../../../common/enums/award-codes";
import { TrendEnum } from "../../../../common/leaf/LeafTrendIndicator/LeafTrendIndicator";
import { ROUTE_PATHS } from "../../../../common/enums/routing-enums";
import { PERSONA_V2 } from "../../../../common/enums/persona";
import defaultContent from "../../../../content/performanceIncomeDashboard";
import commonContent from "../../../../content/common";
import { storedLocale } from "../../../Authentication/util";
import { parseStatus } from "../AnnualPerformanceIncome/utils";
import { useAppSelector } from "../../../../store";
import { getIncentiveViewID } from "../../../../common/util/IncentivesViewID/IncentiveViewID";
import { AWARD_PROGRESS } from "../../../../common/enums/award-progress-codes";
import { ANA_MARKET_CODES } from "../../../../common/constants/market-constants";
import { BootUser } from "../../../../reducers/boot";
import { Config } from "../../../../common/interfaces/config";
import { getCanonicalTitle } from "../../../../common/util/Incentives/IncentiveRegistry";

const getRouteForAwardCode = (awardCode: number, routeId?: string): string | undefined => {
  if (routeId === "") {
    return undefined;
  }

  const routeMap: Record<number, string> = {
    [AWARD_CODES.PERFORMANCE_BONUS]: "",
    [AWARD_CODES.RUBY]: ROUTE_PATHS.PERFORMANCE_BONUS,
    [AWARD_CODES.LEADERSHIP]: ROUTE_PATHS.PERFORMANCE_BONUS,
    [AWARD_CODES.FOSTER]: ROUTE_PATHS.PERFORMANCE_BONUS,
    [AWARD_CODES.DEPTH]: ROUTE_PATHS.PERFORMANCE_BONUS,
    [AWARD_CODES.INTERNATIONAL_LEADERSHIP]: ROUTE_PATHS.PERFORMANCE_BONUS,
    [AWARD_CODES.MONTHLY_Q]: ROUTE_PATHS.MONTHLY_Q,
    [AWARD_CODES.Q_GROWTH_INCENTIVE]: ROUTE_PATHS.Q_GROWTH_INCENTIVE,
    [AWARD_CODES.MONTHLY_PGG]: ROUTE_PATHS.MONTHLY_PGG,
    [AWARD_CODES.PERFORMANCE_PLUS]: ROUTE_PATHS.PERFORMANCE_PLUS,
    [AWARD_CODES.PERFORMANCE_ELITE]: ROUTE_PATHS.PPE,
    [AWARD_CODES.PERFORMANCE_PLUS_ELITE]: ROUTE_PATHS.PPE,
    [AWARD_CODES.PATH_TO_BRONZE]: ROUTE_PATHS.NEW_PATH_TO_BRONZE,
    [AWARD_CODES.BRONZE_FOUNDATION]: ROUTE_PATHS.NEW_BRONZE_FOUNDATION,
    [AWARD_CODES.BRONZE_BUILDER]: ROUTE_PATHS.NEW_BRONZE_BUILDER,
    [AWARD_CODES.MN_BRONZE_FOUNDATION]: ROUTE_PATHS.NEW_BRONZE_FOUNDATION,
    [AWARD_CODES.MN_BRONZE_BUILDER]: ROUTE_PATHS.NEW_BRONZE_BUILDER,
    [AWARD_CODES.CSI]: ROUTE_PATHS.CSI,
    [AWARD_CODES.STRONG_START_INCENTIVE]: ROUTE_PATHS.STRONG_START_INCENTIVE,
    [AWARD_CODES.SSIV2]: ROUTE_PATHS.STRONG_START_INCENTIVE,
    [AWARD_CODES.FOUNDERS_SALES_INCENTIVE]: ROUTE_PATHS.FOUNDERS_SALES_INCENTIVE,
  };

  return routeMap[awardCode];
};

const getContentByPath = (path: string, replacements?: Record<string, string>): string => {
  let keys = path.split(".");
  let value: any = defaultContent.incentives;

  const bronzeIncentivePrefixes = ["ptb", "bfi", "bbi"];
  if (bronzeIncentivePrefixes.includes(keys[0])) {
    const prefix = keys[0];
    const remainingKeys = keys.slice(1);

    let specificValue: any = defaultContent.incentives.bronzeIncentives;
    let foundInSpecific = true;

    for (const key of [prefix, ...remainingKeys]) {
      if (specificValue && typeof specificValue === "object" && key in specificValue) {
        specificValue = specificValue[key];
      } else {
        foundInSpecific = false;
        break;
      }
    }

    if (foundInSpecific && typeof specificValue === "string") {
      value = specificValue;
    } else {
      let sharedValue: any = defaultContent.incentives.bronzeIncentives;
      let foundInShared = true;

      for (const key of remainingKeys) {
        if (sharedValue && typeof sharedValue === "object" && key in sharedValue) {
          sharedValue = sharedValue[key];
        } else {
          foundInShared = false;
          break;
        }
      }

      value = foundInShared && typeof sharedValue === "string" ? sharedValue : path;
    }
  } else {
    for (const key of keys) {
      if (value && typeof value === "object" && key in value) {
        value = value[key];
      } else {
        return path;
      }
    }

    value = typeof value === "string" ? value : path;
  }

  let result = typeof value === "string" ? value : path;

  if (replacements) {
    Object.keys(replacements).forEach((key) => {
      result = result.replace(new RegExp(`\\{${key}\\}`, "g"), replacements[key]);
    });
  }

  return result;
};

const getTranslatedContentByPath = (t: TFunction, path: string, replacements?: Record<string, string>): string => {
  const defaultValue = getContentByPath(path, replacements);

  const bronzeIncentivePrefixes = ["ptb", "bfi", "bbi"];
  const keys = path.split(".");
  let i18nKey: string;

  if (keys.length >= 1 && bronzeIncentivePrefixes.includes(keys[0])) {
    let extractedPath = keys.slice(1).join(".");
    i18nKey = `incentives.bronzeIncentives.${extractedPath}`;
  } else {
    i18nKey = `incentives.${path}`;
  }

  const translated = t(i18nKey, defaultValue, {
    ns: "performanceIncomeDashboard",
    ...(replacements || {}),
  });

  return String(translated);
};

const mapRequirementToKpiTracker = (
  requirement: MonthlyIncomeBreakdownRequirement,
  t: TFunction,
): {
  label: ReactNode;
  percentage?: number;
  achievedSegments?: number;
  totalSegments?: number;
  isMet?: boolean;
  variant?: "progress" | "legs";
} => {
  const contentKey = requirement.ContentKey;

  const replacements: Record<string, string> = {};

  const isBronzeLegRequirement =
    contentKey.includes("ppvLegs") || contentKey.includes("gpvLegs") || contentKey.includes("newLegRequirements");

  if (requirement.Current !== undefined && requirement.Current !== null) {
    const currentValue =
      isBronzeLegRequirement && requirement.Target ? String(requirement.Target) : String(requirement.Current);
    replacements.Current = currentValue;
  }

  if (requirement.Target !== undefined && requirement.Target !== null) {
    replacements.Target = String(requirement.Target);
    replacements.leg = String(requirement.Target);
    replacements.PPV = String(requirement.Target);
    replacements.VCSPV = String(requirement.Target);
    replacements.PCPV = String(requirement.Target);
    replacements.PAPV = String(requirement.Target);
    replacements.perfBonus = String(requirement.Target);
    replacements.vcspv = String(requirement.Target);
  }

  if (isBronzeLegRequirement && requirement.AmountLegMustAchieve != null) {
    replacements.valueTarget = String(requirement.AmountLegMustAchieve);
  }

  if (requirement.LegsTarget) {
    replacements.LegsTarget = String(requirement.LegsTarget);
  }

  if (requirement.Percent !== undefined) {
    replacements.percent = `${requirement.Percent}%`;
  }

  const label = getTranslatedContentByPath(t, contentKey, replacements);

  if (requirement.Variant === "legs") {
    return {
      label,
      achievedSegments: requirement.AchievedSegments || 0,
      totalSegments: requirement.TotalSegments || 0,
      isMet: requirement.Met,
      variant: "legs",
    };
  }

  return {
    label,
    percentage: requirement.Percent || 0,
    isMet: requirement.Met,
    variant: "progress",
  };
};

const mapCorePlanBonusToTile = (
  bonus: MonthlyIncomeBreakdownCorePlanBonus,
  index: number,
  t: TFunction,
): IncomeTileConfig & {
  modalData?: {
    type: "personal" | "differential" | "ruby";
    data: MonthlyIncomeBreakdownCorePlanBonus | MonthlyIncomeBreakdownRubyBonusModal;
    index: number;
  };
} => {
  const contentKey = bonus.ContentKey;

  if (bonus.AwardCode === AWARD_CODES.PERFORMANCE_BONUS && bonus.SubBonuses) {
    const linkRowItems = bonus.SubBonuses.map((subBonus) => {
      const subContentKey = subBonus.ContentKey;
      const isPersonal = subBonus.AwardCode === AWARD_CODES.PERFORMANCE_BONUS_PERSONAL;
      const isDifferential = subBonus.AwardCode === AWARD_CODES.PERFORMANCE_BONUS_DIFFERENTIAL;

      let link = "";

      if (isPersonal) {
        link = "#personal-bonus-modal";
      } else if (isDifferential) {
        link = "#differential-bonus-modal";
      }

      return {
        label: getTranslatedContentByPath(t, subContentKey),
        value: subBonus.BonusAmount,
        link,
        disabled: false,
      };
    });

    const titlePath = `${contentKey}.title`;
    const title = getTranslatedContentByPath(t, titlePath);

    return {
      header: {
        label: title,
        value: bonus.BonusAmount,
      },
      linkRows: {
        items: linkRowItems,
      },
      modalData: {
        type: "personal",
        data: bonus,
        index,
      },
    };
  }

  if (bonus.AwardCode === AWARD_CODES.RUBY && bonus.RubyBonusModal) {
    const titlePath = `${contentKey}.title`;
    const title = getTranslatedContentByPath(t, titlePath);

    return {
      header: {
        label: title,
        labelTestId: `income-tile-label-${contentKey.replace(/\./g, "-")}`,
        value: bonus.BonusAmount,
        link: "#ruby-bonus-modal",
        showArrow: true,
      },
      kpiTrackers: bonus.Requirements
        ? {
            items: bonus.Requirements.map((req) => mapRequirementToKpiTracker(req, t)),
          }
        : undefined,
      modalData: {
        type: "ruby",
        data: bonus.RubyBonusModal,
        index,
      },
    };
  }

  const route = getRouteForAwardCode(
    bonus.AwardCode,
    bonus.Requirements && bonus.Requirements.length > 0 ? "hasRoute" : "",
  );
  const titlePath = `${contentKey}.title`;
  const title = getTranslatedContentByPath(t, titlePath);

  let metricCard;
  if (bonus.AwardCode === AWARD_CODES.FOSTER && bonus.Requirements && bonus.Requirements.length > 0) {
    const leadershipPvKpi = bonus.Requirements.find(
      (req) => req.Type === "KPI" && req.ContentKey === "foster.leadershipPv",
    );
    const leadershipPercentKpi = bonus.Requirements.find(
      (req) => req.Type === "KPI" && req.ContentKey === "foster.leadershipPercent",
    );

    if (leadershipPvKpi && leadershipPercentKpi) {
      const leadershipPv = leadershipPvKpi.Current || "0";
      const leadershipPercent = leadershipPercentKpi.Current || "0%";

      metricCard = {
        items: [
          {
            label: getTranslatedContentByPath(t, "foster.leadershipPv"),
            value: leadershipPv,
          },
          {
            label: getTranslatedContentByPath(t, "foster.leadershipPercent"),
            value: leadershipPercent,
          },
        ],
      };
    }
  }

  const kpiRequirements =
    bonus.Requirements?.filter((req) => req.Type !== "PinRequirement" && req.Type !== "KPI") || [];
  const pinRequirements = bonus.Requirements?.filter((req) => req.Type === "PinRequirement") || [];

  const finalRoute = bonus.AwardCode === AWARD_CODES.FOSTER ? undefined : route;

  return {
    header: {
      label: title,
      labelTestId: `income-tile-label-${contentKey.replace(/\./g, "-")}`,
      value: bonus.BonusAmount,
      link: finalRoute,
      showArrow: !!finalRoute,
    },
    kpiTrackers:
      kpiRequirements.length > 0
        ? {
            items: kpiRequirements.map((req) => mapRequirementToKpiTracker(req, t)),
          }
        : undefined,
    pinRequirements:
      pinRequirements.length > 0
        ? pinRequirements.map((req) => {
            const replacements: Record<string, string> = {};
            if (req.Current) {
              replacements.Current = req.Current;
              replacements.perfBonus = req.Current;
            }
            if (req.Target) replacements.Target = req.Target;
            if (req.Percent !== undefined) replacements.percent = `${req.Percent}%`;

            return {
              met: req.Met || false,
              label: getTranslatedContentByPath(t, req.ContentKey, replacements),
              status: req.Status as any,
            };
          })
        : undefined,
    metricCard,
  };
};

const buildStatusMessage = (
  selectedPeriod: string,
  contentKey: string,
  statusMessageKey: string,
  title: string,
  t: TFunction,
  symbol: string,
): string | undefined => {
  const year = selectedPeriod.substring(0, 4);
  const monthNum = parseInt(selectedPeriod.substring(4, 6), 10) - 1;
  const locale = storedLocale() || "en";
  const monthName = new Intl.DateTimeFormat(locale, { month: "long" }).format(new Date(parseInt(year), monthNum));

  const perIncentiveFallback = getContentByPath(`${contentKey}.${statusMessageKey}`);
  const hasOverride = perIncentiveFallback !== `${contentKey}.${statusMessageKey}`;
  const sharedMessages = defaultContent.incentives.statusMessages as Record<string, string> | undefined;
  const sharedKey = statusMessageKey === "denied" ? "monthlyDenied" : statusMessageKey;
  const fallback = hasOverride ? perIncentiveFallback : (sharedMessages?.[sharedKey] ?? "");
  const i18nKey = hasOverride
    ? `incentives.${contentKey}.${statusMessageKey}`
    : `incentives.statusMessages.${sharedKey}`;

  return t(i18nKey, fallback, {
    ns: "performanceIncomeDashboard",
    title,
    month: monthName,
    year,
    symbol,
  });
};

const mapCorePlusIncentiveToTile = (
  incentive: MonthlyIncomeBreakdownCorePlusIncentive,
  t: TFunction,
  selectedPeriod: string,
  user: BootUser,
  configuration: Config,
  symbol: string,
): IncomeTileConfig => {
  const contentKey = incentive.ContentKey ?? "";
  const route = getRouteForAwardCode(incentive.AwardCode, incentive.RouteId);

  const title = getCanonicalTitle({ id: contentKey, contentKey, awardCode: incentive.AwardCode }, t, {
    selectedPeriod,
    configuration,
    isoCountryCode: user.isoCountryCode,
    aff: user.aff,
    symbol,
  });

  // Handle status messages (denied, held, manuallyApproved, etc.) — same pattern as annual
  if (incentive.StatusMessageKey) {
    const statusBadge =
      incentive.StatusMessageKey === "denied"
        ? AWARD_PROGRESS.NOT_ELIGIBLE
        : incentive.Status
          ? parseStatus(incentive.Status)
          : undefined;
    const statusMessage = buildStatusMessage(
      selectedPeriod,
      incentive.OverrideLabel ?? contentKey,
      incentive.StatusMessageKey,
      title,
      t,
      symbol,
    );

    let statusHeaderValue: string | undefined;
    let statusHeaderTag: AWARD_PROGRESS | undefined;
    if (incentive.AwardCode === AWARD_CODES.SSIV2) {
      const displayLabelToBadge: Record<string, AWARD_PROGRESS> = {
        achieved: AWARD_PROGRESS.ACHIEVED,
        notTracking: AWARD_PROGRESS.NOT_TRACKING,
      };
      const badgeStatus = incentive.DisplayLabel ? displayLabelToBadge[incentive.DisplayLabel] : undefined;
      if (badgeStatus) {
        statusHeaderValue = undefined;
        statusHeaderTag = badgeStatus;
      } else if (incentive.DisplayLabel) {
        statusHeaderValue = incentive.DisplayLabel;
        statusHeaderTag = undefined;
      } else {
        statusHeaderValue = incentive.BonusAmount;
        statusHeaderTag = undefined;
      }
    } else if (incentive.BadgeStatusOnHeader) {
      statusHeaderValue = undefined;
      statusHeaderTag = parseStatus(incentive.BadgeStatusOnHeader);
    } else {
      statusHeaderValue = incentive.BonusAmount;
      statusHeaderTag = undefined;
    }

    return {
      header: {
        label: title,
        labelTestId: `income-tile-label-${contentKey.replace(/\./g, "-")}`,
        value: statusHeaderValue,
        tag: statusHeaderTag,
        link: route,
        showArrow: !!route,
      },
      statusBadge,
      statusMessage,
    };
  }

  const requirements = incentive.Requirements || [];
  const kpiRequirements = requirements.filter((req) => req.Type !== "PinRequirement");
  const pinRequirements = requirements.filter((req) => req.Type === "PinRequirement");

  // Map optional baseline/tracking to legsProgress (same pattern as useIncomeBreakdownTiles)
  let legsProgress: IncomeTileConfig["legsProgress"];
  if (incentive.Baseline != null || incentive.Tracking != null) {
    const items: { label: string; value: string; trend?: TrendEnum; number?: number }[] = [];

    // Q Growth Incentive uses "X Q Months" format and "Current" label instead of "Tracking"
    const isQGrowthIncentive = incentive.AwardCode === AWARD_CODES.Q_GROWTH_INCENTIVE;
    const valueContentKey = isQGrowthIncentive ? "legsProgress.qMonths" : "legsProgress.qMonth";
    const valueDefault = isQGrowthIncentive ? defaultContent.legsProgress.qMonths : defaultContent.legsProgress.qMonth;

    if (incentive.Baseline != null) {
      const baselineLabel = String(
        t("legsProgress.baseline", defaultContent.legsProgress.baseline, { ns: "performanceIncomeDashboard" }),
      );
      const qMonthValue = String(
        t(valueContentKey, valueDefault, {
          ns: "performanceIncomeDashboard",
          value: incentive.Baseline.Formatted,
        }),
      );
      const baselineItem: { label: string; value: string; trend?: TrendEnum; number?: number; testId?: string } = {
        label: baselineLabel,
        value: qMonthValue,
        testId: "core-plus-baseline-row",
      };
      if (incentive.Baseline.MoMTrend) {
        const { Difference, Trend } = incentive.Baseline.MoMTrend;
        baselineItem.trend = Trend;
        baselineItem.number = Difference;
      }
      items.push(baselineItem);
    }
    if (incentive.Tracking != null) {
      // Q Growth Incentive displays "Current" instead of "Tracking"
      const trackingLabelKey = isQGrowthIncentive ? "legsProgress.current" : "legsProgress.tracking";
      const trackingLabelDefault = isQGrowthIncentive
        ? defaultContent.legsProgress.current
        : defaultContent.legsProgress.tracking;
      const trackingLabel = String(t(trackingLabelKey, trackingLabelDefault, { ns: "performanceIncomeDashboard" }));
      const qMonthValue = String(
        t(valueContentKey, valueDefault, {
          ns: "performanceIncomeDashboard",
          value: incentive.Tracking.Formatted,
        }),
      );
      const trackingItem: { label: string; value: string; trend?: TrendEnum; number?: number; testId?: string } = {
        label: trackingLabel,
        value: qMonthValue,
        testId: "core-plus-tracking-row",
      };
      if (incentive.Tracking.MoMTrend) {
        const { Difference, Trend } = incentive.Tracking.MoMTrend;
        trackingItem.trend = Trend;
        trackingItem.number = Difference;
      }
      items.push(trackingItem);
    }
    if (items.length > 0) {
      legsProgress = { items };
    }
  }

  // Resolve header value for SSI V2 (award code 538):
  // When DisplayLabel matches a known AWARD_PROGRESS value, show as badge tag. When absent, show BonusAmount.
  let headerValue: string | undefined;
  let headerTag: AWARD_PROGRESS | undefined;
  if (incentive.AwardCode === AWARD_CODES.SSIV2) {
    const displayLabelToBadge: Record<string, AWARD_PROGRESS> = {
      achieved: AWARD_PROGRESS.ACHIEVED,
      notTracking: AWARD_PROGRESS.NOT_TRACKING,
    };
    const badgeStatus = incentive.DisplayLabel ? displayLabelToBadge[incentive.DisplayLabel] : undefined;
    if (badgeStatus) {
      headerValue = undefined;
      headerTag = badgeStatus;
    } else if (incentive.DisplayLabel) {
      headerValue = incentive.DisplayLabel;
      headerTag = undefined;
    } else {
      headerValue = incentive.BonusAmount;
      headerTag = undefined;
    }
  } else if (incentive.BadgeStatusOnHeader) {
    // API specifies badge should be in header
    headerValue = undefined;
    headerTag = parseStatus(incentive.BadgeStatusOnHeader);
  } else {
    // No header badge - show BonusAmount
    headerValue = incentive.BonusAmount;
    headerTag = undefined;
  }

  return {
    header: {
      label: title,
      labelTestId: `income-tile-label-${contentKey.replace(/\./g, "-")}`,
      value: headerValue,
      tag: headerTag,
      link: route,
      showArrow: !!route,
    },
    legsProgress,
    kpiTrackers:
      kpiRequirements.length > 0
        ? {
            items: kpiRequirements.map((req) => {
              const tracker = mapRequirementToKpiTracker(req, t);

              if (incentive.AwardCode === AWARD_CODES.MONTHLY_Q) {
                if (req.ContentKey === "monthlyQ.monthlyGroupPvWithQLeg") {
                  const q1Label = getTranslatedContentByPath(t, "monthlyQ.q1Label");
                  tracker.label = (
                    <>
                      {tracker.label} <strong>{q1Label}</strong>
                    </>
                  );
                } else if (req.ContentKey === "monthlyQ.monthlyGroupPv") {
                  const qvLabel = getTranslatedContentByPath(t, "monthlyQ.qvLabel");
                  tracker.label = (
                    <>
                      {tracker.label} <strong>{qvLabel}</strong>
                    </>
                  );
                }
              }

              if (
                req.ContentKey === "bfi.personalOrderPv" ||
                req.ContentKey === "bbi.personalOrderPv" ||
                req.ContentKey === "ptb.personalOrderPv"
              ) {
                const label = getTranslatedContentByPath(t, "bronzeIncentives.pcpv", {
                  Current: req.Current,
                  Target: req.Target,
                });
                tracker.label = <>{label}</>;
              }
              return tracker;
            }),
          }
        : undefined,
    pinRequirements:
      pinRequirements.length > 0
        ? pinRequirements.map((req) => {
            const replacements: Record<string, string> = {};
            if (req.Current) {
              replacements.Current = req.Current;
              replacements.perfBonus = req.Current;
            }
            if (req.Target) replacements.Target = req.Target;
            if (req.Percent !== undefined) replacements.percent = `${req.Percent}%`;

            return {
              met: req.Met || false,
              label: getTranslatedContentByPath(t, req.ContentKey, replacements),
              status: req.Status as any,
            };
          })
        : undefined,
    // API specifies badge in body via BadgeStatusOnBody field
    statusBadge: incentive.BadgeStatusOnBody ? parseStatus(incentive.BadgeStatusOnBody) : undefined,
  };
};

// Award codes that belong in the left column for Persona 2 Core Plan layout
const PERSONA2_LEFT_COLUMN_AWARDS = [AWARD_CODES.PERFORMANCE_BONUS, AWARD_CODES.RUBY];

// Award codes that belong in the right column for Persona 2 Core Plan layout
const PERSONA2_RIGHT_COLUMN_AWARDS = [
  AWARD_CODES.LEADERSHIP,
  AWARD_CODES.DEPTH,
  AWARD_CODES.INTERNATIONAL_LEADERSHIP,
  AWARD_CODES.FOSTER,
];

export const useMonthlyIncomeBreakdownMapper = (data: MonthlyIncomeBreakdownResponse | null, personaV2?: number) => {
  const { t } = useTranslation(["performanceIncomeDashboard", "navigation", "common"]);
  const { configuration, user } = useAppSelector((state) => state.boot);
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const symbol = t("disclaimerSymbol", { ns: "common", defaultValue: commonContent.disclaimerSymbol });

  if (!data) return null;

  const modalData: Array<{
    type: "personal" | "differential" | "ruby";
    data: MonthlyIncomeBreakdownCorePlanBonus | MonthlyIncomeBreakdownRubyBonusModal;
    index: number;
  }> = [];

  const corePlanTiles = data.CorePlanBonuses.map((bonus, index) => {
    const tile = mapCorePlanBonusToTile(bonus, index, t);
    if (tile.header) {
      tile.header.tealiumTag = bonus.ContentKey;
    }
    if (tile.modalData) {
      modalData.push(tile.modalData);
      const { modalData: _, ...tileWithoutModalData } = tile;
      return tileWithoutModalData;
    }
    return tile;
  });

  // For Persona 2, split Core Plan tiles into left/right columns
  let corePlanLeftTiles: IncomeTileConfig[] | undefined;
  let corePlanRightTiles: IncomeTileConfig[] | undefined;

  if (personaV2 === PERSONA_V2.BUSINESS_BUILDER || personaV2 === PERSONA_V2.LEADER) {
    const bonusesWithTiles = data.CorePlanBonuses.map((bonus, index) => ({
      bonus,
      tile: corePlanTiles[index],
    }));

    corePlanLeftTiles = bonusesWithTiles
      .filter(({ bonus }) => PERSONA2_LEFT_COLUMN_AWARDS.includes(bonus.AwardCode))
      .map(({ tile }) => tile);

    corePlanRightTiles = bonusesWithTiles
      .filter(({ bonus }) => PERSONA2_RIGHT_COLUMN_AWARDS.includes(bonus.AwardCode))
      .sort(
        (a, b) =>
          PERSONA2_RIGHT_COLUMN_AWARDS.indexOf(a.bonus.AwardCode) -
          PERSONA2_RIGHT_COLUMN_AWARDS.indexOf(b.bonus.AwardCode),
      )
      .map(({ tile }) => tile);
  }

  const corePlusTiles = data.CorePlusIncentives.map((incentive) => {
    const tile = mapCorePlusIncentiveToTile(incentive, t, selectedPeriod, user, configuration, symbol);
    if (tile.header) {
      tile.header.tealiumTag = incentive.ContentKey;

      // Override route based on incentiveViews config (same pattern as menu getFggView)
      const viewId = getIncentiveViewID(incentive.AwardCode, selectedPeriod, configuration, user.isoCountryCode);
      const pages = configuration.cppModPages as unknown as Record<string, string>;
      if (viewId && pages[viewId] && incentive.RouteId !== "") {
        tile.header.link = pages[viewId];
        tile.header.showArrow = true;
      }
    }
    return tile;
  });

  const routeIdToPath: Record<string, string> = {
    calculator: ROUTE_PATHS.EARNINGS_CALCULATOR,
    mbi: ROUTE_PATHS.MBI,
    onlineStatements: ROUTE_PATHS.ONLINE_STATEMENTS,
  };

  const navigationItems = data.NavigationItems.map((item) => ({
    id: item.Id,
    show: item.Show,
    routeId: item.RouteId,
    contentKey: item.ContentKey,
    iconType: item.IconType,
    to: routeIdToPath[item.RouteId] ?? "",
    title: String(
      t(`income.${item.Id}.title`, defaultContent.income[item.Id].title, { ns: "performanceIncomeDashboard" }),
    ),
  }));

  return {
    corePlanTiles,
    corePlanLeftTiles,
    corePlanRightTiles,
    corePlusTiles,
    navigationItems,
    totalIncome: data.TotalIncome,
    modalData,
  };
};
